import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { UploadCloud, FileCheck2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Label } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PRICING = {
  base_price: 29,
  price_per_cm3: 0.05,
  currency: "€",
  materials: [
    { v: "PLA", label: "PLA standard", mult: 1 },
    { v: "PLA+", label: "PLA+ (resistente)", mult: 1.3 },
    { v: "PETG", label: "PETG (tecnico)", mult: 1.5 },
  ],
  qualities: [
    { v: "bassa", label: "Bozza / rapida", mult: 1 },
    { v: "media", label: "Standard", mult: 1.2 },
    { v: "alta", label: "Alta definizione", mult: 1.5 },
  ],
};

function computeEstimate(est, pricing) {
  const w = parseFloat(est.width), d = parseFloat(est.depth), h = parseFloat(est.height);
  const q = parseInt(est.quantity || "1", 10);
  const qty = Number.isNaN(q) ? 1 : Math.max(1, q);
  if (!w || !d || !h || w <= 0 || d <= 0 || h <= 0) return null;
  const volume = w * d * h; // cm³
  const mats = pricing.materials?.length ? pricing.materials : DEFAULT_PRICING.materials;
  const quals = pricing.qualities?.length ? pricing.qualities : DEFAULT_PRICING.qualities;
  const mat = mats.find((m) => m.v === est.material) || mats[0];
  const qual = quals.find((qq) => qq.v === est.quality) || quals[0];
  const perUnit = (pricing.base_price + volume * pricing.price_per_cm3) * mat.mult * qual.mult;
  return Math.round(perUnit * qty);
}

const inputClass =
  "w-full bg-[#0A0E14] border border-white/15 px-4 py-3 text-white placeholder:text-slate-500 font-['Manrope'] focus:outline-none focus:border-[#00C8D7] transition-colors";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [est, setEst] = useState({ width: "", depth: "", height: "", material: "PLA", quality: "media", quantity: "1" });
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then((res) => {
      const p = res.data;
      setPricing(p);
      setEst((s) => ({
        ...s,
        material: p.materials?.[0]?.v || s.material,
        quality: p.qualities?.[Math.min(1, (p.qualities?.length || 1) - 1)]?.v || s.quality,
      }));
    }).catch(() => {});
  }, []);

  const MATERIALS = pricing.materials?.length ? pricing.materials : DEFAULT_PRICING.materials;
  const QUALITIES = pricing.qualities?.length ? pricing.qualities : DEFAULT_PRICING.qualities;
  const CUR = pricing.currency || "€";

  const estimate = computeEstimate(est, pricing);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateEst = (k) => (e) => setEst((s) => ({ ...s, [k]: e.target.value }));

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Il file supera i 25 MB.");
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila nome, email e descrizione del progetto.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      // Estimator fields
      if (est.width && est.depth && est.height) {
        data.append("dimensions", `${est.width}×${est.depth}×${est.height} cm`);
      }
      data.append("material", est.material);
      data.append("quality", est.quality);
      data.append("quantity", est.quantity || "1");
      if (estimate) data.append("estimated_price", String(estimate));
      if (file) data.append("file", file);
      await axios.post(`${API}/quotes`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Richiesta inviata! Ti risponderò a breve.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
      setEst({ width: "", depth: "", height: "", material: "PLA", quality: "media", quantity: "1" });
      setFile(null);
    } catch (err) {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote" className="relative py-24 md:py-32 bg-[#0A0E14] tech-grid-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <Reveal>
            <Label>Richiedi preventivo</Label>
            <h2 className="font-display font-bold text-4xl sm:text-5xl mt-5 tracking-tight leading-[1.05]">
              Parlami del tuo <span className="text-[#00C8D7]">progetto.</span>
            </h2>
            <p className="mt-6 text-slate-400 text-lg leading-relaxed max-w-md">
              Carica il tuo file STL o descrivi cosa ti serve. Valuto la fattibilità e ti rispondo
              con una stima chiara di tempi e costi.
            </p>
            <div className="mt-8 space-y-3">
              {["Risposta rapida e senza impegno", "Nessun ordine minimo elevato", "Consulenza sul file inclusa"].map(
                (t, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 size={18} className="text-[#00C8D7]" />
                    {t}
                  </div>
                )
              )}
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 w-full">
          <Reveal delay={0.1}>
            {done ? (
              <div
                data-testid="quote-success"
                className="bg-[#111827] border border-[#00C8D7]/40 p-12 text-center"
              >
                <CheckCircle2 size={48} className="text-[#00C8D7] mx-auto" />
                <h3 className="font-display font-bold text-2xl mt-6">Richiesta ricevuta!</h3>
                <p className="text-slate-400 mt-3">
                  Grazie. Ho ricevuto la tua richiesta e ti risponderò al più presto.
                </p>
                <button
                  data-testid="quote-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 border border-[#00C8D7] text-[#00C8D7] font-bold px-6 py-3 hover:bg-[#00C8D7]/10 transition-colors"
                >
                  Invia un&apos;altra richiesta
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="quote-form"
                className="bg-[#111827] border border-white/10 p-8 md:p-10 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <input
                    data-testid="quote-name"
                    className={inputClass}
                    placeholder="Nome *"
                    value={form.name}
                    onChange={update("name")}
                  />
                  <input
                    data-testid="quote-email"
                    type="email"
                    className={inputClass}
                    placeholder="Email *"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input
                    data-testid="quote-phone"
                    className={inputClass}
                    placeholder="Telefono (opzionale)"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                  <input
                    data-testid="quote-project-type"
                    className={inputClass}
                    placeholder="Tipo di progetto (es. prototipo)"
                    value={form.project_type}
                    onChange={update("project_type")}
                  />
                </div>
                <textarea
                  data-testid="quote-message"
                  rows={4}
                  className={`${inputClass} resize-none`}
                  placeholder="Descrivi il tuo progetto: dimensioni, quantità, materiale... *"
                  value={form.message}
                  onChange={update("message")}
                />

                {/* Preventivo automatico */}
                <div data-testid="estimator" className="border border-[#00C8D7]/25 bg-[#0A0E14] p-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#00C8D7]">
                      Stima prezzo (opzionale)
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <input data-testid="est-width" type="number" min="0" className={inputClass} placeholder="Largh. cm" value={est.width} onChange={updateEst("width")} />
                    <input data-testid="est-depth" type="number" min="0" className={inputClass} placeholder="Prof. cm" value={est.depth} onChange={updateEst("depth")} />
                    <input data-testid="est-height" type="number" min="0" className={inputClass} placeholder="Alt. cm" value={est.height} onChange={updateEst("height")} />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 mt-3">
                    <select data-testid="est-material" className={inputClass} value={est.material} onChange={updateEst("material")}>
                      {MATERIALS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                    </select>
                    <select data-testid="est-quality" className={inputClass} value={est.quality} onChange={updateEst("quality")}>
                      {QUALITIES.map((q) => <option key={q.v} value={q.v}>{q.label}</option>)}
                    </select>
                    <input data-testid="est-quantity" type="number" min="1" className={inputClass} placeholder="Quantità" value={est.quantity} onChange={updateEst("quantity")} />
                  </div>
                  <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4">
                    <div>
                      <span className="font-mono-tech text-xs text-slate-500 uppercase tracking-[0.15em]">Stima indicativa</span>
                      <p data-testid="est-price" className="font-display font-black text-3xl text-[#00C8D7] leading-none mt-1">
                        {estimate ? `~ ${estimate}${CUR}` : "—"}
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 max-w-[55%] text-right leading-snug">
                      Prezzo indicativo, confermato dopo la valutazione del file.
                    </p>
                  </div>
                </div>

                {/* File upload */}
                <label
                  data-testid="quote-file-drop"
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files?.[0]); }}
                  className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed px-6 py-8 cursor-pointer transition-colors ${
                    drag ? "border-[#00C8D7] bg-[#00C8D7]/5" : "border-white/15 hover:border-[#00C8D7]/50"
                  }`}
                >
                  <input
                    ref={fileRef}
                    data-testid="quote-file-input"
                    type="file"
                    accept=".stl,.obj,.3mf,.step,.stp,.zip"
                    className="hidden"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                  />
                  {file ? (
                    <span className="flex items-center gap-2 text-[#00C8D7] font-medium">
                      <FileCheck2 size={20} /> {file.name}
                    </span>
                  ) : (
                    <>
                      <UploadCloud size={28} className="text-slate-400" />
                      <span className="text-slate-300 text-sm">
                        Trascina qui il tuo file STL o <span className="text-[#00C8D7]">sfoglia</span>
                      </span>
                      <span className="font-mono-tech text-xs text-slate-500">STL · OBJ · 3MF · max 25MB</span>
                    </>
                  )}
                </label>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  data-testid="quote-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-8 py-4 hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <><Loader2 size={20} className="animate-spin" /> Invio in corso...</>
                  ) : (
                    "Invia richiesta"
                  )}
                </motion.button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
